import { zodResolver } from "@hookform/resolvers/zod";
import { type AccessRequestInput, accessRequestInput } from "@mola/shared";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { Check, ClipboardList, ListChecks, Quote } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ToothIcon } from "@/components/tooth-icon";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { authClient } from "@/lib/auth-client";
import { apiUrl, errorMessage } from "@/lib/http";
import { cn } from "@/lib/utils";
import { Code } from "@/plugins/verification/codes";

export const Route = createFileRoute("/")({
  // Signed-in users have nothing to do on a marketing page; the same check
  // `/login` makes, pointed the other way.
  beforeLoad: async () => {
    const { data } = await authClient.getSession();
    if (data) throw redirect({ to: "/agents" });
  },
  component: Home,
});

// The case first, as the app leads with it; questions on GOZ and BEMA are the
// second use. In the words a practice would use, and no wider than the check
// is: statutory insurance, Zahnersatz, crowns and bridges.
const features = [
  {
    icon: ClipboardList,
    title: "Kostenvoranschlag",
    text: "Den Fall in eigenen Worten beschreiben: mola ermittelt Befunde nach der Festzuschuss-Richtlinie, Positionen nach BEMA und GOZ und den Eigenanteil – derzeit für Kronen und Brücken bei gesetzlich Versicherten.",
  },
  {
    icon: ListChecks,
    title: "Bestätigen",
    text: "Was mola annimmt, steht als Vorschlag da. Sie bestätigen jede Angabe einzeln, erst dann steht der Kostenvoranschlag – mit einer Zusammenfassung für die Praxis zum Drucken.",
  },
  {
    icon: Quote,
    title: "Belegen",
    text: "Jede Aussage mit ihrer Fundstelle aus GOZ, BEMA, BZÄK-Kommentar, Beratungsforum und G-BA-Richtlinien – auch bei Fragen zu einzelnen Positionen, mit einem Klick an der zitierten Stelle.",
  },
];

// A finished case of the app (14–16 bridge, crown on 36, 70 % Bonus), as the
// card's result table (since removed) showed it on 2026-10-22 with the KZV BW
// point values: real figures, so the page makes no claim the app would
// contradict. Redo them if the prices or the rules change.
const exampleRows = [
  { place: "Brücke 14–16", befunde: ["2.1", "2.7"], gesamt: "1.172,33 €" },
  { place: "Zahn 36", befunde: ["1.1"], gesamt: "394,25 €" },
  { place: "Oberkiefer, Behandlungsfall", befunde: [], gesamt: "75,80 €" },
];
const exampleSums = [
  { label: "Gesamt, ohne Material", amount: "1.642,38 €" },
  { label: "Festzuschuss, 70 % (Bonus)", amount: "− 1.109,41 €" },
  { label: "Eigenanteil", amount: "532,97 €", strong: true },
];

function RequestAccess() {
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<AccessRequestInput>({
    resolver: zodResolver(accessRequestInput),
    defaultValues: { email: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    const response = await fetch(apiUrl("/api/access/requests"), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email: values.email }),
    });
    if (!response.ok) {
      setFormError(
        await errorMessage(response, "Das hat nicht geklappt. Bitte versuchen Sie es erneut."),
      );
      return;
    }
    setDone(true);
  });

  if (done) {
    return (
      <div className="max-w-md space-y-4">
        <p role="status" className="text-sm leading-relaxed">
          Danke. Wenn wir Ihren Zugang freischalten, erhalten Sie eine E-Mail mit einem Link zum
          Festlegen des Passworts.
        </p>
        <Link to="/login" className="text-sm underline decoration-accent underline-offset-4">
          Bereits freigeschaltet? Anmelden
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="max-w-sm space-y-4" noValidate>
      <Field label="E-Mail" htmlFor="email" error={form.formState.errors.email?.message}>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          className="h-10"
          {...form.register("email")}
        />
      </Field>
      {formError ? (
        <p role="alert" className="text-destructive text-sm">
          {formError}
        </p>
      ) : null}
      <Button type="submit" className="h-11 px-6 md:h-11" disabled={form.formState.isSubmitting}>
        {form.formState.isSubmitting ? "Wird gesendet…" : "Zugang anfragen"}
      </Button>
      <p>
        <Link to="/login" className="text-sm underline decoration-accent underline-offset-4">
          Bereits freigeschaltet? Anmelden
        </Link>
      </p>
    </form>
  );
}

function Home() {
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-12 md:px-8 md:py-20">
      <div className="grid items-center gap-12 md:grid-cols-[1.1fr_1fr]">
        <div className="space-y-6">
          <h1 className="font-semibold text-4xl leading-[1.1] tracking-tight md:text-5xl">
            Vom Fall zum
            <br />
            Kostenvoranschlag.
          </h1>
          <p className="max-w-md text-lg text-muted-foreground leading-relaxed">
            Beschreiben Sie den Fall in eigenen Worten: mola ermittelt Befunde, Festzuschüsse und
            Positionen nach BEMA und GOZ. Sie bestätigen jede Angabe, bevor der Kostenvoranschlag
            steht.
          </p>
          <RequestAccess />
        </div>

        {/* A worked example rather than a screenshot: it stays legible in both
            themes and at every width, and draws the case's result as the card
            does (the codes as chips, confirmed as a green check). */}
        <figure
          aria-label="Beispiel eines Kostenvoranschlags"
          className="space-y-4 rounded-xl border bg-background p-5"
        >
          <p className="ml-auto w-fit max-w-[85%] rounded-xl bg-muted px-4 py-2.5 text-sm">
            Gesetzlich versichert: 36 braucht eine Krone, 15 fehlt, Brücke von 14 auf 16 geplant.
          </p>
          <div className="space-y-3 text-sm">
            <p className="flex items-center gap-1.5 font-medium text-muted-foreground text-xs">
              <ToothIcon className="size-3" />
              mola
            </p>
            <p className="flex items-center gap-1.5">
              <Check aria-hidden strokeWidth={3} className="size-3.5 text-confirmed" />
              Kostenvoranschlag, alle Angaben bestätigt
            </p>
            <table className="w-full text-left tabular-nums">
              <tbody className="divide-y">
                {exampleRows.map((row) => (
                  <tr key={row.place}>
                    <td className="py-1.5 pr-2">{row.place}</td>
                    <td className="py-1.5 pr-2">
                      <span className="inline-flex gap-1">
                        {row.befunde.map((b) => (
                          <Code key={b}>{b}</Code>
                        ))}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-1.5 text-right">{row.gesamt}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t">
                {exampleSums.map((sum) => (
                  <tr key={sum.label} className={cn(sum.strong && "border-t font-semibold")}>
                    <td
                      colSpan={2}
                      className={cn("py-1.5", !sum.strong && "text-muted-foreground")}
                    >
                      {sum.label}
                    </td>
                    <td className="whitespace-nowrap py-1.5 text-right">{sum.amount}</td>
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
        </figure>
      </div>

      <ul className="mt-20 grid gap-8 md:grid-cols-3">
        {features.map(({ icon: Icon, title, text }) => (
          <li key={title} className="space-y-2">
            <Icon aria-hidden className="size-5 text-accent" />
            <h2 className="font-semibold text-xl">{title}</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">{text}</p>
          </li>
        ))}
      </ul>

      <p className="mt-20 border-t pt-6 text-muted-foreground text-xs">
        mola unterstützt bei der Abrechnung. Die Verantwortung für Kostenvoranschläge und Rechnungen
        bleibt bei der Praxis.
      </p>
    </div>
  );
}
